import 'core-js/stable';
import 'bootstrap';
import { ErrorBoundary } from 'components/common/ErrorBoundary';
import React, { FC } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router';
import { initSGConnect } from 'services/SgConnect';
import { AppRoutes } from 'routes';
import { DebouncerProvider } from 'components/common/DebouncerProvider';
import { AppContextProvider } from 'components/common/AppContextProvider';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { Environment, getEnvironment } from 'config/constants';
import { configureRum } from 'config/rum';
import { MaterialUiProvider } from 'components/common/MaterialUiProvider';

import './css/bootstrapTheming.scss';
import './css/site.scss';
import 'react-loading-skeleton/dist/skeleton.css';

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            staleTime: 60 * 60 * 1000, // reload data after 1 hour
            gcTime: 60 * 60 * 1000, // remove data by GC if data unused/inactive more than 1 hour
        },
    },
});

const isDev = getEnvironment() === Environment.Local || Environment.Development;

const App: FC = () => (
    <QueryClientProvider client={queryClient}>
        <DebouncerProvider>
            <MaterialUiProvider>
                <ErrorBoundary>
                    <BrowserRouter>
                        <AppContextProvider>
                            <AppRoutes />
                            {isDev && <ReactQueryDevtools buttonPosition="bottom-right" initialIsOpen={false} />}
                        </AppContextProvider>
                    </BrowserRouter>
                </ErrorBoundary>
            </MaterialUiProvider>
        </DebouncerProvider>
    </QueryClientProvider>
);

async function start() {
    const renderApp = () => {
        const container = document.getElementById('root');
        const root = createRoot(container!);
        root.render(<App />);
        const appInitLoader = document.getElementById('app-init-loader');
        if (appInitLoader) {
            appInitLoader.remove();
        }
    };

    configureRum();

    initSGConnect();

    renderApp();
}

start();
